import { GenerateImageOptions, GenerateImageRendererOptions } from "@centricsoftware/model-viewer";
import JsonResponse from "csi/comm/JsonResponse";
import { cnl } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import Writer from "csi/xml/Writer";
import { action, computed, makeObservable, observable } from "mobx";
import { createContext, useContext } from "react";
import { SnapshotTemplateId, SnapshotTemplateOptions } from "../snapshotUtils";
import { ComponentItem } from "designSystem/types/component-item-types";

export type OnSaveCallback = (response: JsonResponse, url?: cnl) => void
export default class SnapshotTemplateDialogStore extends ReactionStore {
	/** handles saving data on the form */
	readonly onSave: OnSaveCallback;

	/** handles cancel button click */
	readonly onCancel: VoidFunction;
		
	/** writer object from the controller */
	readonly writer: Writer;

	/** Id of the template type */
	readonly templateId: SnapshotTemplateId;

	/** Image options */
	@observable.ref imageOptions: GenerateImageOptions = null;

	/** Renderer options */
	@observable.ref rendererOptions: GenerateImageRendererOptions = null;

	/** Style template options */
	@observable.ref styleTemplateOptions: SnapshotTemplateOptions = null;

	/** Style type options */
	@observable.ref productTypesOptions: ComponentItem<any>[] = null;

	/** Determine if there is a product type error when saving a new template */
	@observable productTypeErrorMessage: string | null = null;

	/** Validate style snapshot template options */
	@computed get canSaveStyleSnapshotTemplate(): boolean {
		if (!this.styleTemplateOptions || !this.rendererOptions || !this.imageOptions || this.productTypeErrorMessage || !this.productTypesOptions?.length) {
			return false;
		}	
		const { templateName } = this.styleTemplateOptions;
		const { imageType, imageRatio, environment } = this.rendererOptions;
		const { camera: { cameraPositions }, materials: { sides, primitives } } = this.imageOptions;
		const isTurntable = this.imageOptions.camera.turntable !== undefined;

		// validate that the template name is not and empty string
		const isTemplateNameValid = templateName !== "";
		const isCameraPositionValid = !isTurntable ? cameraPositions.length > 0 : true;
		const isImageCountValid = isTurntable ? this.imageOptions.camera.turntable?.numberOfImages : true;
		const hasImageType = imageType !== undefined;
		const hasImageRatio = imageRatio !== undefined;
		const hasEnvironment = environment !== null;
		let hasMaterial = true;
		let hasBaseGeometry = true;

		if (this.templateId === SnapshotTemplateId.Material) {
			hasMaterial = sides.length > 0;
			hasBaseGeometry = primitives.length > 0;
		}

		return isTemplateNameValid && isCameraPositionValid && isImageCountValid && hasImageType && hasImageRatio && hasEnvironment && hasMaterial && hasBaseGeometry;
	};

	/** Set the renderer options */
	@action setRendererOptions = (rendererOptions: GenerateImageRendererOptions) => {
		this.rendererOptions = rendererOptions;
	};

	/** Set the image Options */
	@action setImageOptions = (imageOptions: GenerateImageOptions) => {
		this.imageOptions = imageOptions;
	};

	/** set style template options */
	@action setTemplateOptions = (options: SnapshotTemplateOptions) => {
		this.styleTemplateOptions = options;
	};

	/** Style type options */
	@action setProductTypesOptions= (options: ComponentItem<any>[]) => {
		this.productTypesOptions = options;
	};

	/** Set if there is a productType Error */
	@action setProductTypeError = (error: string | null) => {
		this.productTypeErrorMessage = error;
	};

	constructor(onCancel:VoidFunction, onSave: OnSaveCallback, writer:Writer, id: SnapshotTemplateId) {
		super();
		makeObservable(this);

		this.onCancel = onCancel;
		this.onSave = onSave;
		this.writer = writer;
		this.templateId = id;
	}
}

export const SnapshotTemplateDialogStoreContext = createContext<SnapshotTemplateDialogStore>(undefined);
export const useSnapshotTemplateDialogStore = (): SnapshotTemplateDialogStore => useContext(SnapshotTemplateDialogStoreContext);